import type { Persona } from "@/api/personas";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { AgentCard } from "./manage-personas/AgentCard";
import { Card } from "./ui/card";
import { H6 } from "./ui/h6";
import { Separator } from "./ui/separator";
import { CAPABILITY_CARD_COLORS } from "@/components/manage-personas/capabilities/CapabilityCard";
import { useGetAgentContentCountQuery } from "@/api/content-review";
import { automationQueries } from "@/api/automations";
import { useQuery } from "@tanstack/react-query";
import { creditQueries } from "@/api/credits";
import { useEffect, useState } from "react";
import { agentQueries } from "@/api/api";

export function PersonaDetailsHeader({
  // Adding personaId here allows navigation actions to be functional before persona data is fully loaded. Avoids undefined persona problems.
  personaId,
  personaData,
}: {
  personaId: string;
  personaData: Persona;
}) {
  const [visibleCapabilities, setVisibleCapabilities] = useState(3);

  useEffect(() => {
    const updateVisibleCapabilities = () => {
      const width = window.innerWidth;
      if (width >= 1536) {
        // 2xl breakpoint
        setVisibleCapabilities(4);
      } else if (width >= 1280) {
        // xl breakpoint
        setVisibleCapabilities(2);
      } else if (width >= 1024) {
        // lg breakpoint
        setVisibleCapabilities(2);
      } else {
        setVisibleCapabilities(1);
      }
    };

    updateVisibleCapabilities();

    window.addEventListener("resize", updateVisibleCapabilities);

    return () => {
      window.removeEventListener("resize", updateVisibleCapabilities);
    };
  }, []);

  const { data } = useQuery(agentQueries.agent(personaId));
  const canEdit = data?.can_edit_persona;

  const { data: contentCount, isLoading: isContentCountLoading } =
    useGetAgentContentCountQuery(personaId);

  const { data: creditBalance } = useQuery({
    ...creditQueries.agentBalance(personaId),
  });

  const { data: agentAutomations = [] } = useQuery({
    ...automationQueries.automations(personaData.persona_id),
    enabled: !!personaData.persona_id,
  });

  const capabilitiesEnabled = agentAutomations.filter(
    (automation) => automation.enabled,
  );

  return (
    <div className="w-full flex md:justify-between justify-start sm:flex-row flex-col md:items-center gap-2">
      <AgentCard
        key={personaData.persona_id}
        agent={personaData}
        index={0}
        link={false}
      />
      <Card className="w-full h-56 bg-black/40 border-zinc-800 p-6 rounded-2xl hover:bg-black/60 flex">
        <div className="flex flex-row justify-between gap-4 w-full">
          {canEdit && (
            <>
              <div className="flex flex-col gap-y-2 p-2 w-full">
                <span className="font-light text-[#ABABAB] text-2xl">
                  {new Intl.NumberFormat().format(
                    creditBalance?.available_credits || 0,
                  )}
                </span>
                <H6 className="font-bold uppercase">Available Credits</H6>
                <Button variant="outline" className="text-accent  w-44" asChild>
                  <Link to={`/account#credits`}>Buy More Credits</Link>
                </Button>
              </div>
              <Separator orientation="vertical" className="hidden xl:block" />
            </>
          )}
          <div className="flex-col gap-y-2 p-2 w-full hidden xl:flex">
            {(canEdit || personaData.public_persona) && (
              <span className={"font-light text-[#ABABAB] text-2xl"}>
                {isContentCountLoading ? (
                  <div className="opacity-0">100</div>
                ) : (
                  new Intl.NumberFormat().format(contentCount || 0)
                )}
              </span>
            )}
            <H6 className="font-bold uppercase">Content Created</H6>
            {canEdit && (
              <Button variant="outline" className="text-accent w-44" asChild>
                <Link to={`/create`}>Create Content</Link>
              </Button>
            )}
          </div>
          <Separator orientation="vertical" className="hidden xl:block" />
          <div className="flex-col gap-y-2 p-2 w-full hidden xl:flex">
            {(canEdit || personaData.public_persona) && (
              <span className="font-light text-[#ABABAB] text-2xl">
                {capabilitiesEnabled.length}
              </span>
            )}
            <H6 className="font-bold  uppercase">Capabilities enabled</H6>
            <div className="flex flex-row gap-x-3 flex-wrap text-xs">
              {capabilitiesEnabled
                .slice(0, visibleCapabilities)
                .map((capability, index) => (
                  <p
                    key={index}
                    style={{
                      color:
                        CAPABILITY_CARD_COLORS[
                          index % CAPABILITY_CARD_COLORS.length
                        ],
                    }}
                  >
                    {capability.name}
                  </p>
                ))}
              {capabilitiesEnabled.length > visibleCapabilities && (
                <p className="text-white">
                  {capabilitiesEnabled.length - visibleCapabilities} more...
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default PersonaDetailsHeader;
